import { useSelect } from "downshift";
import { useState, ComponentType } from "react";
import type {
  SelectBoxItem,
  ItemRenderProps,
  DisabledItemRenderProps,
  SelectBoxComponents,
} from "./types";

export interface SelectBoxInput {
  items: SelectBoxItem[];
  hashFunction: (item: SelectBoxItem) => string | number;
  ItemRenderComponent: ComponentType<ItemRenderProps>;
  onChangeEvent?: (args: { value: string | number }) => void;
  matchSelectedItem: SelectBoxItem | undefined;
  isItemDisabled?: (item: SelectBoxItem) => boolean;
  renderDisabledItem?: ComponentType<DisabledItemRenderProps>;
  components: SelectBoxComponents;
  /** Accessible name for the toggle button (e.g. "Start Year"). */
  ariaLabel?: string;
}

const SelectBox = ({
  items,
  hashFunction,
  ItemRenderComponent,
  onChangeEvent,
  matchSelectedItem,
  isItemDisabled,
  renderDisabledItem: RenderDisabledItem,
  ariaLabel,
  components: {
    SelectBoxContainer,
    DropdownContainer,
    ToggleButton,
    ArrowUp,
    ArrowDown,
    MainMenu,
    SelectedItemLabel,
  },
}: SelectBoxInput) => {
  function ComboBox() {
    const [displayItems, setDisplayItems] = useState(items);

    const {
      getToggleButtonProps,
      getLabelProps,
      getMenuProps,
      getItemProps,
      selectedItem,
      highlightedIndex,
      isOpen,
      selectItem,
    } = useSelect({
      onSelectedItemChange({ selectedItem }) {
        if (selectedItem) {
          if (
            !matchSelectedItem ||
            matchSelectedItem.value !== selectedItem.value
          ) {
            onChangeEvent?.({ value: selectedItem.value });
          }
        }
      },
      items: displayItems,
      itemToString(item) {
        return item ? String(hashFunction(item)) : "";
      },
    });

    if (matchSelectedItem && !selectedItem) {
      selectItem(matchSelectedItem);
    }

    // Name the control "Start Year, 2020" etc. Undefined when no ariaLabel is
    // passed, leaving other consumers on downshift's default naming.
    const accessibleName =
      ariaLabel && selectedItem ? `${ariaLabel}, ${selectedItem.label}` : ariaLabel;

    return (
      <SelectBoxContainer>
        <DropdownContainer
          role="button"
          {...getToggleButtonProps()}
          // Set after the spread so it beats downshift's aria-labelledby, which
          // points at the value label and is empty when nothing is selected.
          {...(accessibleName
            ? { "aria-label": accessibleName, "aria-labelledby": undefined }
            : {})}
          className={isOpen ? "dropdown-open" : ""}
        >
          <SelectedItemLabel {...getLabelProps()}>
            {selectedItem ? selectedItem.label : ""}
          </SelectedItemLabel>
          {/* Decorative chevron -- the container above is the real toggle.
              Hide this handler-less button from AT so it isn't exposed as an
              unlabelled "empty button"; clicks still bubble to the container. */}
          <ToggleButton type="button" tabIndex={-1} aria-hidden>
            {isOpen ? <ArrowUp /> : <ArrowDown />}
          </ToggleButton>
        </DropdownContainer>
        <MainMenu
          {...getMenuProps()}
          style={{ display: isOpen ? "block" : "none" }}
        >
          {isOpen &&
            displayItems.map((item, index) => {
              if (
                isItemDisabled &&
                RenderDisabledItem &&
                isItemDisabled(item)
              ) {
                return (
                  <RenderDisabledItem
                    key={`dropdown_item_${item.value}`}
                    item={item}
                    hashFunction={hashFunction}
                  />
                );
              }
              return (
                <ItemRenderComponent
                  key={`dropdown_item_${item.value}`}
                  item={item}
                  index={index}
                  highlightedIndex={highlightedIndex}
                  selectedItem={selectedItem}
                  getItemProps={getItemProps}
                  hashFunction={hashFunction}
                  displayItems={displayItems}
                  setDisplayItems={setDisplayItems}
                />
              );
            })}
        </MainMenu>
      </SelectBoxContainer>
    );
  }
  return <ComboBox />;
};

export default SelectBox;
